import { z } from 'zod';

const WarningItemSchema = z.object({
  position: z.coerce.number().int().min(1).max(7),
  questionCopy: z.string(),
  answerNotRequiringWarning: z.enum(['Yes', 'No']),
  answerRequiringWarning: z.enum(['Yes', 'No']),
  warning: z.string(),
});

type WarningItem = z.infer<typeof WarningItemSchema>;

export const WarningDataSchema = z
  .object({
    riskIncomeTax: WarningItemSchema,
    riskInheritanceTax: WarningItemSchema,
    riskFutureContributions: WarningItemSchema,
    riskOtherProducts: WarningItemSchema,
    riskMaintainLifestyle: WarningItemSchema,
    riskInvestElsewhere: WarningItemSchema,
    riskWithGuarantees: WarningItemSchema,
  })
  .refine(
    (data) => {
      const positions = Object.values(data).map((item) => item.position);
      return new Set(positions).size === positions.length;
    },
    {
      message: 'Each warning item must have a unique position',
      path: ['position'],
    },
  );

type WarningData = z.infer<typeof WarningDataSchema>;

export type WarningDataQuestion = keyof WarningData;

export type RiskAnswers =
  | WarningItem['answerNotRequiringWarning']
  | WarningItem['answerRequiringWarning']
  | '';

export const warningData: WarningData = {
  riskIncomeTax: {
    position: 1,
    questionCopy:
      'Are you aware of the income tax implications of withdrawing from a personal pension?',
    answerNotRequiringWarning: 'Yes',
    answerRequiringWarning: 'No',
    warning:
      'You can usually withdraw up to 25% of your pension pot tax-free. If you withdraw more than this you’ll pay tax on your withdrawals. How much you pay depends on your total income for the tax year and your tax band. Depending on the amount of income from this and other sources, this could put you into a higher tax band. If you invest money from your pension in another savings product, you may have to pay tax on your returns.',
  },
  riskInheritanceTax: {
    position: 2,
    questionCopy:
      'Are you aware that withdrawing from a personal pension could have implications for Inheritance Tax, bankruptcy proceedings and means-tested benefits?',
    answerNotRequiringWarning: 'Yes',
    answerRequiringWarning: 'No',
    warning:
      "If you withdraw money from your pension, it may be included as part of your estate. As a result, your beneficiaries may have to pay Inheritance Tax on your death, your creditors may be able to claim any withdrawn sums if you're declared bankrupt, and any withdrawals will usually be taken into account when your entitlement to means-tested benefits (like housing benefit or income support) is calculated. Leaving your money invested in your pension may be more favourable. Please visit our Pensions Explained Centre to learn more.",
  },
  riskFutureContributions: {
    position: 3,
    questionCopy:
      'Do you intend to pay future contributions in excess of £10,000 a year into your pension?',
    answerNotRequiringWarning: 'No',
    answerRequiringWarning: 'Yes',
    warning:
      "If you take a taxable payment from your pension then your annual limit for making tax relievable contributions to your pension is reduced to £10,000. This is known as the Money Purchase Annual Allowance (MPAA). If you contribute more than £10,000 into your pension in a single year, you'll face a tax charge.",
  },
  riskOtherProducts: {
    position: 4,
    questionCopy:
      'Are you aware that other pension products are available to pay your pension income and there may be more suitable options if there are any aspects of your health or lifestyle which may shorten your life expectancy?',
    answerNotRequiringWarning: 'Yes',
    answerRequiringWarning: 'No',
    warning:
      'There are many different pension options and products available to you which may suit your needs. For example, an enhanced annuity may give you higher payments if you have a medical condition that reduces your life expectancy, although you’ll want to consider the tax efficiencies of leaving your funds in a pension. You should seek financial advice or guidance from Pension Wise if you’re unsure which product may be most suitable for you.',
  },
  riskMaintainLifestyle: {
    position: 5,
    questionCopy:
      'Are you expecting your pension withdrawals to maintain your lifestyle throughout your retirement?',
    answerNotRequiringWarning: 'No',
    answerRequiringWarning: 'Yes',
    warning:
      'Your pension is supposed to provide you with an income during your retirement. This could mean it needs to support you for many years. PensionBee isn’t an annuity provider and doesn’t provide a guaranteed income. When you’re deciding how much money to withdraw and which pension product to buy, think carefully about how much you’ll need to support you in retirement. You should seek independent financial advice or guidance from Pension Wise if you’re unsure which product may be most suitable for you.',
  },
  riskInvestElsewhere: {
    position: 6,
    questionCopy:
      'Are you considering withdrawing funds from your pension to invest elsewhere?',
    answerNotRequiringWarning: 'No',
    answerRequiringWarning: 'Yes',
    warning:
      "If you're investing your money, you should be aware of any relevant implications, including charges, taxes and scams. Scammers may promise you impossibly high returns on your investment, or tell you that you can access more than 25% of your pension tax-free. Scammers will often cold call you or send unsolicited emails and texts. Always check that the company you're engaging with is regulated and authorised by the Financial Conduct Authority (FCA).",
  },
  riskWithGuarantees: {
    position: 7,
    questionCopy:
      'Are you intending to transfer a pension which contains guarantees as part of your withdrawal plans?',
    answerNotRequiringWarning: 'No',
    answerRequiringWarning: 'Yes',
    warning:
      'The PensionBee personal pension doesn’t provide any guaranteed salary-related income, annuity rates or income nor other non-standard features. Any such features associated with any other existing pensions you may hold won’t apply.',
  },
};
